import { Injectable, inject } from '@angular/core';
import { DataLookupService } from '@app/common/services/data-api/data-lookup.service';
import { PlexEnvironment } from '@app/env';
import { AssetTeamId } from '@app/shared/data/project-enums';
import { DrillingCompletionObject, ProjectObject } from 'app/cem/project/content/project-create-new/project-create.models';
import { LookupProjectTemplateService } from 'app/cem/project/services/lookups/lookup-project-template.service';
import { ColumnMap, TypedDataResult } from 'app/common/services/data-api/column-map';
import { ProjectTemplate, Table } from 'app/shared/models';
import { NameValueInt, NameValueIntParentInt } from 'app/shared/models/name-value';
import { Subject } from 'rxjs';

@Injectable()
export class ProjectTemplateMatrixService {
  private apiService = inject(LookupProjectTemplateService);
  private lookupService = inject(DataLookupService);
  private environment = inject(PlexEnvironment);

  public templates: ProjectTemplate[];
  public filtered: ProjectTemplate[];
  public filterValue: ProjectTemplate = new ProjectTemplate();
  public templateAsync: Promise<ProjectTemplate[]>;
  private templateAsyncResolver: (value?: ProjectTemplate[] | PromiseLike<ProjectTemplate[]>) => void;

  public items: ProjectTemplateMetaFields = new ProjectTemplateMetaFields();
  public filteredItems: ProjectTemplateMetaFields = new ProjectTemplateMetaFields();
  public ready: Subject<boolean> = new Subject();

  public constructor() {
    this.templateAsync = new Promise((resolve, reject) => {
      this.templateAsyncResolver = resolve;
    });

    let dest: Array<string> = [
      'AssetTeamIds',
      'ConstructionSequenceIds',
      'ConstructionTypeIds',
      'DivisionIds',
      'DrillDesignIds',
      'FunctionIds',
      'ProjectTypeIds'
    ];

    let pstColumns: ColumnMap[] = [
      new ColumnMap('name', 'Description'),
      new ColumnMap('value', 'ProjectSubTypeId'),
      new ColumnMap('parentValue', 'ProjectTypeId'),
      new ColumnMap('isActive', 'IsActive')
    ];

    let preferredProjectTypes = new Set<number>([21, 22, 58, 59, 60, 61, 62, 13]);

    Promise.all([
      this.apiService.getTemplates(),
      this.lookupService.getNameValue(Table.AssetTeams, null, null, true),
      this.lookupService.getNameValue(Table.ConstructionSequences, null, null, true),
      this.lookupService.getNameValue(Table.ConstructionTypes, null, null, true),
      this.lookupService.getNameValue(Table.Divisions, null, null, true),
      this.lookupService.getNameValue(Table.DrillDesigns, null, null, true),
      this.lookupService.getNameValue(Table.Functions, null, null, true),
      this.lookupService.getNameValue(Table.ProjectTypes, null, null, true).then(pts => {
        let preferred = [];
        let other = [];
        pts.forEach(p => {
          if (preferredProjectTypes.has(p.value)) {
            preferred.push(p);
          } else {
            other.push(p);
          }
        })
        preferred = preferred.sort((a, b) => this.sortByName(a, b));
        other = other.sort((a, b) => this.sortByName(a, b));
        let result = [...preferred, ...other];
        return result;
      }),
      this.lookupService.get(Table.ProjectSubTypes, pstColumns, true, true, '')
    ]
    ).then(r => {
      let templates: TypedDataResult<ProjectTemplate> = r[0] as any;
      this.templates = templates.data;

      //name value ints
      for (let i = 0; i < dest.length; i++) {
        let values = r[i + 1]; //+1 because we already consumed index 0
        let fieldName = dest[i];
        this.items[fieldName] = values;
      }

      //and finally project sub types
      this.items.ProjectSubTypeIds = r[r.length - 1] as any;

      if (!this.items.AssetTeamIds || !this.items.AssetTeamIds.length) {
        throw new Error('Asset Teams were not returned or properly set in project-template-matrix service. This represents a significant blocking error in the API, Database or a logic error in this component.');
      }
      this.templateAsyncResolver(this.templates);
      this.ready.next(true);
    });
  }

  private notDefined(item: any): boolean {
    return item === null || item === undefined || item === 0 || isNaN(item);
  }

  public same(one: any, two: any): boolean {
    return !this.diff(one, two);
  }
  public diff(one: any, two: any): boolean {

    //handle nulls/undefineds first
    if (one === null && two === null) return false;
    if (one === undefined && two === undefined) return false;
    if (!one && two) return true;
    if (!two && one) return true;

    //now handle properties
    let oneNames = Object.getOwnPropertyNames(one);
    let twoNames = Object.getOwnPropertyNames(two);
    if (oneNames.length !== twoNames.length) return true;

    for (let i = 0; i < oneNames.length; i++) {
      let name = oneNames[i];
      if (!twoNames.includes(name)) {
        return true;
      }
      if (one[name] !== two[name]) return true;
    }
    return false;
  }

  private lastFilter: ProjectTemplate = null;

  public filter(filterby: ProjectTemplate): ProjectTemplate[] {
    if (!this.templates || !this.templates.length) return this.templates;
    if (!filterby) return this.templates;

    if (!this.diff(this.lastFilter, filterby)) return this.templates;

    if (!this.filterValue) {
      this.filterValue = filterby;
    } else if (filterby !== this.filterValue) {
      let copy = Object.assign({}, this.filterValue);
      Object.assign(this.filterValue, filterby);
      if (!this.diff(copy, this.filterValue)) {
        return this.templates;
      }
    }
    this.lastFilter = Object.assign({}, filterby);

    //filter the list of ProjectTemplats to thise with values matching your filter
    this.filtered = this.filterBy(this.filterValue);

    //now we have to filter all the meta objects using the unique values returned
    //first get distinct IDs
    //then get values from metadata using the ids as a filter

    let mapByField: Map<string, Set<number>> = new Map();

    //go through the items and get the possible filters
    //you cannot go through filters first, because you can't filter by that one item's value (i.e., AssetTeamIds) to get the available items

    Object.getOwnPropertyNames(this.items).forEach((fieldName: string, idx2, array2) => {
      //singular
      let singName = fieldName.substr(0, fieldName.length - 1);
      //now, filter templates by everything but this
      let multiFilterValue = Object.assign({}, this.filterValue);
      multiFilterValue[singName] = null;
      let filteredExceptX = this.filterBy(multiFilterValue);
      this.mapUnique(mapByField, filteredExceptX, singName);
    });

    //now mapByField has unique Ids for every field Type that is in items setting the filteredItems to the possible values from items
    mapByField.forEach((set: Set<number>, fieldName: string, map: Map<string, Set<number>>) => {
      let plural = fieldName + 's';             //example: AssetTeamIds
      let source: any[] = this.items[plural];   //these are the items you care about
      if (source && source.length) {            //ex: source is the array of ALL AssetTeamIds
        //filter source using the unique values i the set
        let dest: any[];
        if (source[0].hasOwnProperty('parentValue')) {
          //use that as filter
          //special case, must filter by ProjectTypeId against 'parentValue'
          //TODO: dest = source.filter((x: NameValueIntParentInt) => set.has(x.parentValue));
          dest = source.filter((x: NameValueIntParentInt) => x.parentValue === this.filterValue.ProjectTypeId);
        } else {
          dest = source.filter((x: NameValueInt) => set.has(x.value));      //ex: return only AssetTeamIds from source that are in the filteed set of values
        }
        if (fieldName === 'ProjectTypeId') {
          this.filteredItems[plural] = dest; //already sorted.sort((a, b) => this.sortByName(a, b));

        } else {
          this.filteredItems[plural] = dest.sort((a, b) => this.sortByName(a, b));

        }
      }
    });
    //NOW filteredItems[...] has only items that match the above filter
  }

  public sortByName(a: { name: string }, b: { name: string }) {
    const genreA = a.name && a.name.toUpperCase();
    const genreB = b.name && b.name.toUpperCase();
    let comparison = 0;
    if (genreA > genreB) {
      comparison = 1;
    } else if (genreA < genreB) {
      comparison = -1;
    }
    return comparison;
  }

  private mapUnique(mapByField: Map<string, Set<number>>, source: ProjectTemplate[], limitToField?: string) {
    source.forEach((pt: ProjectTemplate, idx: number, array: ProjectTemplate[]) => {
      Object.getOwnPropertyNames(pt).forEach((fieldName: string, idx2, array2) => {
        if (!limitToField || limitToField === fieldName) {
          if (fieldName.endsWith('Id') && this.items.hasOwnProperty(fieldName + 's')) {
            //we only care about ...Id fields that are part of our [items] object.
            let set: Set<number>;
            if (!mapByField.has(fieldName)) {
              set = new Set();
              mapByField.set(fieldName, set);
            }
            else {
              set = mapByField.get(fieldName);
            }
            let valueOf: any = pt[fieldName]; //example: pt.AssetTeamId = 3
            if (valueOf !== null) {
              //filter by it even if zero
              if (!set.has(valueOf)) {
                set.add(valueOf);
              }
            }
          }
        }
      });
    });
  }

  private filterBy(filter: ProjectTemplate): ProjectTemplate[] {
    let result: ProjectTemplate[] = [];

    if (this.environment.showDelawareBasin && filter.AssetTeamId === AssetTeamId.DEL) {
      result = this.templates.filter(t => t.AssetTeamId === filter.AssetTeamId);
    } else {
      result = this.templates.filter(
        t =>
          (this.notDefined(t.AssetTeamId) || t.AssetTeamId === filter.AssetTeamId || this.notDefined(filter.AssetTeamId)) &&
          (this.notDefined(t.DivisionId) || t.DivisionId === filter.DivisionId || this.notDefined(filter.DivisionId)) &&
          (this.notDefined(t.DrillDesignId) || t.DrillDesignId === filter.DrillDesignId || this.notDefined(filter.DrillDesignId)) &&
          (this.notDefined(t.FunctionId) || t.FunctionId === filter.FunctionId || this.notDefined(filter.FunctionId)) &&
          (this.notDefined(t.OperatingUnitId) || t.OperatingUnitId === filter.OperatingUnitId || this.notDefined(filter.OperatingUnitId)) &&
          (this.notDefined(t.ProjectSubTypeId) || t.ProjectSubTypeId === filter.ProjectSubTypeId || this.notDefined(filter.ProjectSubTypeId)) &&
          (this.notDefined(t.ProjectTypeId) || t.ProjectTypeId === filter.ProjectTypeId || this.notDefined(filter.ProjectTypeId)) &&
          (this.notDefined(t.ScheduleTemplateNameId) || t.ScheduleTemplateNameId === filter.ScheduleTemplateNameId || this.notDefined(filter.ScheduleTemplateNameId))
      );
    }

    return result;
  }

  public updateOneItemValue(selectedValue: number, fieldName: string): void {
    let found: boolean = false;
    let changed: boolean = false;

    if (this.filterValue && this.filterValue.hasOwnProperty(fieldName)) {
      let valueOf: any = this.filterValue[fieldName];
      found = true;
      changed = valueOf !== selectedValue;
    }
    if (!found || !changed) {
      return;
    }

    this.filterValue[fieldName] = selectedValue;
    this.filter(this.filterValue);
  }

  public getLists(filtered: boolean = true): Map<string, any[]> {
    let source = filtered ? this.filteredItems : this.items;

    let result: Map<string, any[]> = new Map();
    Object.getOwnPropertyNames(source).forEach((fieldName: string, idx, array) => {
      result.set(fieldName, source[fieldName]);
    });
    return result;
  }

  public findProjectTemplate(o: ProjectObject, fromFiltered: boolean = false): ProjectTemplate {
    let source: ProjectTemplate[] = fromFiltered ? this.filtered : this.templates;

    let found = source.find(
      x =>
        x.AssetTeamId === o.AssetTeamId &&
        x.FunctionId === o.FunctionId &&
        x.DivisionId === o.DivisionId &&
        x.OperatingUnitId === o.OperatingUnitId &&
        x.ProjectTypeId === o.ProjectTypeId
    );
    return found;
  }

  public async findDrillingTemplate(o: DrillingCompletionObject, fromFiltered: boolean = true): Promise<ProjectTemplate> {
    let source: ProjectTemplate[] = fromFiltered ? this.filtered : this.templates;

    if (fromFiltered && source.length === 0) source = this.templates;
    let selectedServer: ProjectTemplate = await this.apiService.findTemplate(o.ProjectId, o.ProjectTypeId, o.ProjectSubTypeId,
      o.DrillDesignId, o.AssetTeamId, o.ConstructionTypeId,
      o.FunctionId, o.OperatingUnitId, o.DivisionId, o.DefaultStandardConfigurationId
    );

    if (!selectedServer) return Promise.resolve(null);

    let selected: ProjectTemplate;

    if (source && source.length) {
      selected = source.find(e => e.ProjectTemplateId === selectedServer.ProjectTemplateId);
      if (!selected) {
        selected = selectedServer;
      }
    } else {
      selected = selectedServer;
    }
    return Promise.resolve(selected);
  }

  public updateFunctions: {
    [key: string]: (item: ProjectTemplate, value: number) => boolean;
  } = {
      AssetTeamId: (item: ProjectTemplate, value: number) => {
        if (item && item.AssetTeamId !== value) {
          item.AssetTeamId = value;
          return true;
        } else return true;
      },
      ProjectTypeId: (item: ProjectTemplate, value: number) => {
        if (item && item.ProjectTypeId !== value) {
          item.ProjectTypeId = value;
          return true;
        } else return true;
      },
      ConstructionTypeId: (item: ProjectTemplate, value: number) => {
        if (item && item.ConstructionTypeId !== value) {
          item.ConstructionTypeId = value;
          return true;
        } else return true;
      },
      DivisionId: (item: ProjectTemplate, value: number) => {
        if (item && item.DivisionId !== value) {
          item.DivisionId = value;
          return true;
        } else return true;
      },
      DrillDesignId: (item: ProjectTemplate, value: number) => {
        if (item && item.DrillDesignId !== value) {
          item.DrillDesignId = value;
          return true;
        } else return true;
      },
      FunctionId: (item: ProjectTemplate, value: number) => {
        if (item && item.FunctionId !== value) {
          item.FunctionId = value;
          return true;
        } else return true;
      },
      OperatingUnitId: (item: ProjectTemplate, value: number) => {
        if (item && item.OperatingUnitId !== value) {
          item.OperatingUnitId = value;
          return true;
        } else return true;
      },
      ProjectSubTypeId: (item: ProjectTemplate, value: number) => {
        if (item && item.ProjectSubTypeId !== value) {
          item.ProjectSubTypeId = value;
          return true;
        } else return true;
      }

    };

  public updateOneItemValueX(updater: (item: ProjectTemplate, value: number) => any, newValue: number): boolean {
    //EXAMPLE: this.updateOneItemValueX(this.updateFunctions.AssetTeamId, newValue);
    return updater(this.filterValue, newValue);
  }

  public async updateOneItemValueXAsync(updater: (item: ProjectTemplate, value: number) => any, newValue: number): Promise<boolean> {
    //EXAMPLE: this.updateOneItemValueX(this.updateFunctions.AssetTeamId, newValue);
    if (updater(this.filterValue, newValue)) {
      return this.filterAsync(this.filterValue).then(r => {
        return true;
      });
    }
    return Promise.resolve(false);
  }

  public async filterAsync(filterby: ProjectTemplate): Promise<ProjectTemplate[]> {
    if (this.templates) {
      this.filtered = this.filter(filterby);
      return Promise.resolve(this.filtered);
    }

    //still waiting for data
    return this.templateAsync.then(t => {
      if (t) {
        this.filtered = this.filter(filterby);
        return this.filtered;
      }
    });
  }
}

export interface IProjectTemplateMetaFields {
  AssetTeamIds?: NameValueInt[];
  ConstructionSequenceIds?: NameValueInt[];
  ConstructionTypeIds?: NameValueInt[];
  DivisionIds?: NameValueInt[];
  DrillDesignIds?: NameValueInt[];
  FunctionIds?: NameValueInt[];
  ProjectSubTypeIds?: NameValueIntParentInt[];
  ProjectTypeIds?: NameValueInt[];
}
export class ProjectTemplateMetaFields implements IProjectTemplateMetaFields {

  AssetTeamIds?: NameValueInt[] = [];
  ConstructionSequenceIds?: NameValueInt[] = [];
  ConstructionTypeIds?: NameValueInt[] = [];
  DivisionIds: NameValueInt[] = [];
  DrillDesignIds?: NameValueInt[] = [];
  FunctionIds?: NameValueInt[] = [];
  ProjectSubTypeIds?: NameValueIntParentInt[] = [];
  ProjectTypeIds?: NameValueInt[] = [];

}